// Dexie (IndexedDB) local store for MAKAI offline-first.
// Client-only. Never import from server functions or SSR loaders.
import Dexie, { type Table } from "dexie";

export interface CachedRow {
  id: string;
  table: string;
  data: unknown;          // plain JSON snapshot from server
  updated_at: string;     // ISO; used as sync watermark
  encrypted?: boolean;
  cipher?: string;        // base64 ciphertext when encrypted
  iv?: string;            // base64 IV
  user_id?: string;       // scope to current user
  fetched_at: number;     // epoch ms
}

export type OutboxOp = "insert" | "update" | "delete" | "rpc";

export interface OutboxItem {
  id?: number;
  op: OutboxOp;
  table: string;          // supabase table or RPC name
  payload: unknown;       // row or rpc args
  match?: Record<string, unknown>; // for update/delete .match() clause
  dedupeKey?: string;     // collapse duplicate offline edits
  /** Optimistic-concurrency guard: skip the write when the server row is newer. */
  guard?: { idColumn: string; idValue: unknown; versionColumn: string; versionValue: string | null };
  user_id?: string;
  created_at: number;
  attempts: number;
  last_error?: string;
  status: "pending" | "syncing" | "failed";
}

export interface SyncMeta {
  table: string;
  last_pulled_at: string | null;  // ISO
  last_synced_at: number;         // epoch ms
}

export interface DownloadRecord {
  id: string;                // resource id
  kind: "lesson" | "video" | "book" | "pdf" | "paper" | "assignment" | "resource";
  title: string;
  url: string;
  cache_key: string;         // CacheStorage key
  size_bytes: number;
  downloaded_at: number;
  updated_at: string | null;
  user_id?: string;
}

export type PackKind = "lesson" | "video" | "quiz" | "paper" | "assignment" | "ai_notes";

/** A compressed, self-contained bundle of learning content usable offline. */
export interface ContentPack {
  id: string;                // `${kind}:${resourceId}`
  kind: PackKind;
  resource_id: string;
  title: string;
  subtitle?: string;
  blob: Blob;                // gzip-compressed JSON payload
  compressed: boolean;
  raw_bytes: number;
  size_bytes: number;        // stored (compressed) size
  version: string | null;    // server updated_at at download time
  downloaded_at: number;
  user_id?: string;
}

export interface SyncConflict {
  id?: number;
  table: string;
  row_id: string;
  reason: string;
  local: unknown;
  detected_at: number;
  resolution: "kept_server" | "kept_local";
}

export interface KV {
  key: string;
  value: unknown;
  updated_at: number;
}

class MakaiOfflineDB extends Dexie {
  rows!: Table<CachedRow, string>;        // composite-keyed via [table+id]
  outbox!: Table<OutboxItem, number>;
  sync_meta!: Table<SyncMeta, string>;
  downloads!: Table<DownloadRecord, string>;
  packs!: Table<ContentPack, string>;
  conflicts!: Table<SyncConflict, number>;
  kv!: Table<KV, string>;

  constructor() {
    super("makai_offline_v1");
    this.version(1).stores({
      rows: "id, table, user_id, updated_at, fetched_at",
      outbox: "++id, status, table, dedupeKey, created_at",
      sync_meta: "table",
      downloads: "id, kind, user_id, downloaded_at",
      kv: "key",
    });
    this.version(2).stores({
      rows: "id, table, user_id, updated_at, fetched_at",
      outbox: "++id, status, table, dedupeKey, created_at",
      sync_meta: "table",
      downloads: "id, kind, user_id, downloaded_at",
      packs: "id, kind, resource_id, user_id, downloaded_at",
      conflicts: "++id, table, row_id, detected_at",
      kv: "key",
    });
  }
}

let _db: MakaiOfflineDB | null = null;

/** Get the singleton Dexie instance. Returns null on server / SSR. */
export function offlineDB(): MakaiOfflineDB | null {
  if (typeof window === "undefined" || typeof indexedDB === "undefined") return null;
  if (!_db) _db = new MakaiOfflineDB();
  return _db;
}

/** Wipe all offline data — call on logout. */
export async function wipeOfflineDB() {
  const db = offlineDB();
  if (!db) return;
  await Promise.all([
    db.rows.clear(),
    db.outbox.clear(),
    db.sync_meta.clear(),
    db.downloads.clear(),
    db.packs.clear(),
    db.conflicts.clear(),
    db.kv.clear(),
  ]);
}

/** Approximate IndexedDB storage usage in bytes. */
export async function estimateStorage(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}
